/**
 * Reports — the sealed artifacts and their history (IA spec §7).
 *
 * The load-bearing distinction, pinned by tests: opening a report can end
 * three ways, and they must render as three DIFFERENT things —
 *
 *  - verified            → the artifact serves, with its seal hash shown;
 *  - generator_superseded → a CALM version fact, not an error: "sealed by an
 *                           older generator; the sealed artifact remains
 *                           valid; this engine does not regenerate it";
 *  - integrity_failure   → a LOUD refusal: verification failed, the report
 *                           does not render — no content, no warning-banner-
 *                           over-content (§7: fail closed).
 *
 * A UI that rendered both 409s as "couldn't load this report" would turn a
 * correctness feature into a bug report.
 *
 * The comparison is rendered under its own derived-read label — computed at
 * read time from two sealed artifacts, never presented as an assessment.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { errorDetail } from '../services/api';
import { fmtTs } from '../utils/complianceFormat';
import RunAssessmentModal from '../components/compliance/RunAssessmentModal';

function errCode(err) {
  const d = err?.response?.data?.detail;
  return d && typeof d === 'object' ? d.code : null;
}
function errMessage(err) {
  const d = err?.response?.data?.detail;
  return d && typeof d === 'object' ? d.message : null;
}

function VerifyState({ state }) {
  if (!state) return null;
  if (state.kind === 'verified') {
    return (
      <div data-testid={`verify-ok-${state.id}`} className="text-xs text-green-300 mt-2">
        Seal verified by regeneration — the served document matches its recorded seal.
      </div>
    );
  }
  if (state.kind === 'generator_superseded') {
    return (
      <div data-testid={`verify-superseded-${state.id}`}
           className="text-xs text-gray-300 mt-2 border-l-2 border-gray-500 pl-2">
        Sealed by an older generator. The sealed artifact remains valid and its delivered
        renderings stand; this engine does not regenerate it. This is a version fact, not
        an error. {state.message}
      </div>
    );
  }
  return (
    <div data-testid={`verify-failed-${state.id}`}
         className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded-lg p-3 mt-2">
      <div className="font-semibold">Verification failed — this report will not render.</div>
      <div className="text-xs mt-1">
        An input no longer matches what was sealed. This is an integrity event, not a
        loading problem. {state.message}
    </div>
    </div>
  );
}

export default function ComplianceReports() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verify, setVerify] = useState({});
  const [picked, setPicked] = useState([]);
  const [running, setRunning] = useState(false);
  const [cmp, setCmp] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get('/skills/compliance/reports');
      if (!mounted.current) return;
      setDoc(data);
    } catch (err) {
      if (!mounted.current) return;
      setError(errorDetail(err, 'Reports could not be listed.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    try {
      await api.get(`/skills/compliance/reports/${id}.json`);
      setVerify((v) => ({ ...v, [id]: { kind: 'verified', id } }));
    } catch (err) {
      const code = errCode(err);
      setVerify((v) => ({
        ...v,
        [id]: { kind: code === 'generator_superseded' ? 'generator_superseded'
                     : code === 'integrity_failure' ? 'integrity_failure'
                     : 'integrity_failure',
                id, message: errMessage(err) || errorDetail(err, '') },
      }));
    }
  };

  const pick = (id) => {
    setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p.slice(-1), id]);
  };

  const compare = async () => {
    setCmp(null);
    const { data } = await api.get(
      `/skills/compliance/reports/compare?a=${picked[0]}&b=${picked[1]}`);
    setCmp(data);
  };

  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-1">
        <h1 className="text-2xl font-bold text-white">Reports</h1>
        <button data-testid="run-assessment" onClick={() => setRunning(true)}
                className="px-3 py-1.5 text-sm rounded-lg bg-blue-700 text-white hover:bg-blue-600">
          Run assessment
        </button>
      </div>
      {doc && <p className="text-sm text-gray-400 mb-4">{doc.note}</p>}

      {loading && <div className="text-gray-400 text-sm">Loading reports…</div>}
      {error && (
        <div data-testid="reports-error" className="bg-red-900/30 border border-red-800 rounded-xl p-5 text-sm text-red-200">
          {error}
        </div>
      )}

      {!loading && !error && doc && (
        <>
          {picked.length === 2 && (
            <button data-testid="compare-btn" onClick={compare}
                    className="mb-3 px-3 py-1.5 text-sm rounded-lg border border-blue-600 text-blue-300 hover:bg-blue-900/30">
              Compare the two selected reports
            </button>
          )}
          <div className="space-y-3">
            {doc.reports.map((r) => {
              const v = verify[r.report_id];
              const refused = v?.kind === 'integrity_failure';
              if (r.stored_seal === 'failed') {
                // The stored row no longer matches its recorded seal: render
                // AS refused — no metadata from the altered document, no
                // downloads, no compare checkbox. The rest of the list stands.
                // In place, as a ROW among rows — nine rows, one refused;
                // never eight rows plus an alarm. Full seal, unabridged.
                return (
                  <div key={r.report_id} data-testid={`report-${r.report_id.slice(0, 8)}`}
                       className="bg-gray-800 rounded-xl border border-red-800 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white">
                          Report {r.report_id.slice(0, 8)} · sealed {fmtTs(r.created_at)} · {r.generator_version}
                        </div>
                        <div className="text-xs text-red-200 mt-1 font-semibold">
                          Verification failed — this report will not render.
                        </div>
                        <div className="text-xs text-red-300 mt-1">
                          The stored document no longer matches its recorded seal — an
                          integrity event, not a loading problem.
                        </div>
                        <div className="text-xs text-gray-400 mt-1 font-mono break-all">
                          recorded seal {r.report_document_sha256}
                        </div>
                      </div>
                      <span className="inline-block px-2 py-0.5 text-[11px] rounded-full border bg-red-900/40 text-red-300 border-red-700 whitespace-nowrap">
                        refused
                      </span>
                    </div>
                  </div>
                );
              }
              return (
                <div key={r.report_id} data-testid={`report-${r.report_id.slice(0, 8)}`}
                     className="bg-gray-800 rounded-xl border border-gray-700 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white">
                        <input type="checkbox" className="mr-2 align-middle"
                               checked={picked.includes(r.report_id)}
                               onChange={() => pick(r.report_id)} />
                        {r.entity} · {r.evidence_period?.start?.slice(0, 10)} → {r.evidence_period?.end?.slice(0, 10)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Generated {fmtTs(r.created_at)} · {r.generator_version} · seal <span className="font-mono">{r.report_document_sha256.slice(0, 16)}…</span>
                      </div>
                      <div className="text-xs text-gray-300 mt-1">
                        {(r.determination_summary || []).filter((s) => s.count > 0)
                          .map((s) => `${s.count} ${s.rendered_as.toLowerCase()}`).join(' · ')}
                      </div>
                      <VerifyState state={v} />
                    </div>
                    <div className="flex flex-col items-end gap-1 text-xs">
                      <button onClick={() => open(r.report_id)} className="text-blue-300 underline">
                        Verify &amp; open
                      </button>
                      {v?.kind === 'verified' && !refused && (
                        <>
                          <a className="text-gray-300 underline" href={`${api.defaults.baseURL}/skills/compliance/reports/${r.report_id}.pdf`}>PDF</a>
                          <a className="text-gray-300 underline" href={`${api.defaults.baseURL}/skills/compliance/reports/${r.report_id}.html`} target="_blank" rel="noreferrer">HTML</a>
                          <a className="text-gray-300 underline" href={`${api.defaults.baseURL}/skills/compliance/reports/${r.report_id}.json`} target="_blank" rel="noreferrer">JSON</a>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {cmp && (
            <div data-testid="comparison" className="mt-6 bg-gray-800 rounded-xl border border-gray-700 p-5">
              <div data-testid="derived-label" className="text-xs text-gray-400 italic mb-1">{cmp.label}</div>
              {cmp.boundary_statement && (
                <div data-testid="comparison-boundary" className="text-xs text-gray-400 border-l-2 border-gray-600 pl-2 mb-3">
                  {cmp.boundary_statement}
                </div>
              )}
              <div className="text-xs text-gray-400 mb-3">
                <span className="font-mono">{cmp.earlier.report_id.slice(0, 8)}</span> ({fmtTs(cmp.earlier.created_at)})
                {' → '}
                <span className="font-mono">{cmp.later.report_id.slice(0, 8)}</span> ({fmtTs(cmp.later.created_at)})
              </div>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-gray-500 border-b border-gray-700">
                  <th className="py-1 pr-3">Requirement</th><th className="py-1 pr-3">Was</th><th className="py-1">Is</th>
                </tr></thead>
                <tbody>
                  {cmp.transitions.map((t) => (
                    <tr key={t.requirement_id} data-testid={`trans-${t.requirement_id}`}
                        className={`border-b border-gray-800 ${t.changed ? 'text-white' : 'text-gray-500'}`}>
                      <td className="py-1 pr-3 font-mono">{t.requirement_id}{t.changed && ' *'}</td>
                      <td className="py-1 pr-3">{t.was_gloss}</td>
                      <td className="py-1">{t.is_gloss}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {cmp.inputs_supplied_between.length > 0 && (
                <div className="text-xs text-gray-300 mt-3">
                  Inputs supplied between the two seals:{' '}
                  {cmp.inputs_supplied_between.map((f) => `${f.kind} (${f.fact_id}, ${f.asserted_by})`).join('; ')}
                </div>
              )}
              {cmp.new_findings.length > 0 && (
                <div className="text-xs text-red-300 mt-2">New findings: {cmp.new_findings.map((f) => f.condition).join('; ')}</div>
              )}
              {cmp.resolved_findings.length > 0 && (
                <div className="text-xs text-green-300 mt-2">Resolved findings: {cmp.resolved_findings.map((f) => f.condition).join('; ')}</div>
              )}
            </div>
          )}
        </>
      )}
      {running && (
        <RunAssessmentModal onClose={() => setRunning(false)}
                            onDone={() => load()} />
      )}
    </div>
    </div>
  );
}
